import React from "react";
import { SeoHead } from "@/components/seo/SeoHead";
import { PageContainer } from "@/components/ui/PageContainer";
import { ContentLayout } from "@/components/content/ContentLayout";
import "@/styles/print/article-print.css";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { CheckCircle2, Sliders, Flag, Globe, ChartNoAxesCombined, Building2, BadgeDollarSign } from "lucide-react";

const GettingStartedAlternatives2: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Getting Started with Alternatives — A Practical Guide"
        description="A narrative, action-first guide for Canadians: choose your path (platforms, advisors, banks, liquid alts), confirm eligibility, assess readiness, do focused due diligence, and ship a starter plan."
        canonicalPath="/content/can/getting-started-alternative-v2"
      />

      <header className="border-b border-border/60 bg-slate-900 text-slate-50">
        <PageContainer center>
          <div className="py-8 md:py-10">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Getting Started with Alternatives</h1>
            <p className="mt-2 text-slate-300 text-base md:text-lg">A confident, step-by-step way to add private markets without the noise.</p>
          </div>
        </PageContainer>
      </header>

      <main>
        <ContentLayout>
          <section className="prose prose-slate max-w-none py-8 prose-p:leading-7 prose-p:text-[15px]">
            <p>
              If you're exploring private equity, real estate, private credit, or hedge strategies, the hardest part is knowing where to begin — and how much to do on your own. This guide gives you a simple decision path, a readiness check, and a minimal set of diligence steps so you can take action with clarity.
            </p>

            <div className="not-prose mt-4 rounded-xl border border-dashed hover:border-solid border-muted-foreground/30 bg-gradient-to-br from-white to-muted/20 p-4 shadow-sm transition-shadow hover:shadow-xl">
              <div className="grid md:grid-cols-[1fr_auto] items-start gap-4">
                <div className="text-sm text-foreground">
                  <div className="font-semibold mb-1">TL;DR</div>
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    <li>Pick an access path (platforms, advisor, bank, liquid alts) that matches your time, capital, and support needs.</li>
                    <li>Confirm eligibility (Accredited or OM) and choose account wrapper (TFSA/RRSP/Taxable) before screening deals.</li>
                    <li>Do focused diligence: Strategy • Team • Terms. Avoid guaranteed returns and rushed timelines.</li>
                    <li>Start small (5–10%), diversify, and review quarterly. Scale only after real experience.</li>
                    <li>Use tools to keep it simple — plan first, then allocate.</li>
                  </ul>
                </div>
                <div className="justify-self-end self-end flex gap-2">
                  <Link to="/tools/eligibility-test"><Button variant="default" className="rounded-md bg-[#2D4C7F] hover:bg-[#243a66] text-white">Eligibility Test</Button></Link>
                  <Link to="/portfolio/tools"><Button variant="secondary" className="rounded-md">Open WealthKit</Button></Link>
                </div>
              </div>
            </div>

            <h2 className="mt-10">Choose Your Path</h2>
            <p>Different paths work for different constraints. Use this quick guide and click through for depth.</p>

            <div className="not-prose grid md:grid-cols-2 gap-3 mt-3">
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-semibold"><ChartNoAxesCombined className="h-4 w-4 text-muted-foreground" /> Platforms (EMDs)</div>
                <p className="mt-1 text-sm text-muted-foreground">Lower minimums, direct access, transparent fees.</p>
                <div className="text-[13px] mt-2"><span className="font-medium">Best when:</span> you want control and can do your own diligence.</div>
                <div className="text-[13px]"><span className="font-medium">Watch out:</span> illiquidity (3–7+ yrs), time required.</div>
                <div className="mt-3"><Link to="/content/can/marketplace-guide" className="underline text-primary">See Canadian marketplaces →</Link></div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-semibold"><Building2 className="h-4 w-4 text-muted-foreground" /> Advisors</div>
                <p className="mt-1 text-sm text-muted-foreground">Curated shelves, integrated planning, institutional due diligence.</p>
                <div className="text-[13px] mt-2"><span className="font-medium">Best when:</span> capital is larger and you prefer delegation.</div>
                <div className="text-[13px]"><span className="font-medium">Watch out:</span> higher minimums/fees, limited selection by firm.</div>
                <div className="mt-3"><Link to="/glossary/provincial-regulators" className="underline text-primary">Verify registrations →</Link></div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-semibold"><Globe className="h-4 w-4 text-muted-foreground" /> Banks (Proprietary)</div>
                <p className="mt-1 text-sm text-muted-foreground">Brand trust and convenience within your bank.</p>
                <div className="text-[13px] mt-2"><span className="font-medium">Best when:</span> simplicity and existing relationships matter.</div>
                <div className="text-[13px]"><span className="font-medium">Watch out:</span> proprietary menus, capacity constraints.</div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-semibold"><BadgeDollarSign className="h-4 w-4 text-muted-foreground" /> Liquid Alts (ETFs/MFs)</div>
                <p className="mt-1 text-sm text-muted-foreground">Alternative strategies with daily liquidity.</p>
                <div className="text-[13px] mt-2"><span className="font-medium">Best when:</span> you’re getting familiar or need flexibility.</div>
                <div className="text-[13px]"><span className="font-medium">Watch out:</span> returns can differ from private versions; MERs higher than plain-vanilla ETFs.</div>
              </div>
            </div>

            <h2 className="mt-10">Preflight Readiness</h2>
            <p className="mb-3">Five quick checks before you wire a dollar:</p>
            <div className="not-prose grid sm:grid-cols-2 gap-2">
              {[
                "Emergency fund in place (3–6 months)",
                "Time horizon ≥ 5–10 years for private deals",
                "No near-term liquidity needs for this capital",
                "Eligibility confirmed (Accredited or OM)",
                "Account wrapper chosen (TFSA/RRSP/Taxable)",
              ].map((item) => (
                <div key={item} className="flex items-start gap-2 rounded-md border bg-muted/20 p-2 text-[13px]">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                  <div>{item}</div>
                </div>
              ))}
            </div>

            <h2 className="mt-10">Are You Eligible?</h2>
            <p className="mb-2">Many private investments require <strong>Accredited Investor</strong> status; others use <strong>OM exemptions</strong> with limits and suitability checks.</p>
            <ul>
              <li>Income: $200K+ individual ($300K+ with spouse), expected this year too</li>
              <li>Financial assets: $1M+ (pre-tax, net of liabilities)</li>
              <li>Net assets: $5M+</li>
            </ul>
            <p className="mt-2"><Link to="/tools/eligibility-test" className="underline text-primary">Take the eligibility test →</Link></p>

            <h2 className="mt-10">Two Simple Case Studies</h2>
            <div className="not-prose grid md:grid-cols-2 gap-3">
              <div className="rounded-lg border bg-card p-4">
                <div className="font-semibold">Starter Portfolio (10% Alts on $100K)</div>
                <ul className="mt-2 list-disc pl-5 text-[13px] space-y-1">
                  <li>5% Liquid Alts ETF in TFSA (learn the strategy, daily liquidity)</li>
                  <li>5% Platform deal (RRSP eligible if possible) in private credit or real estate</li>
                </ul>
                <div className="mt-2 text-[13px]"><span className="font-medium">Outcome:</span> exposure + experience with bounded risk.</div>
                <div className="text-[13px]"><span className="font-medium">Watch:</span> fees, illiquidity timelines, distribution tax slips.</div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="font-semibold">Scale-Up (250K+ Deployable)</div>
                <ul className="mt-2 list-disc pl-5 text-[13px] space-y-1">
                  <li>Real assets and private credit as anchors (e.g., 30–40%)</li>
                  <li>Selective growth (private equity/venture) for upside (e.g., 10–20%)</li>
                  <li>Liquid Alts for flexibility and rebalancing (e.g., 10–20%)</li>
                </ul>
                <div className="mt-2 text-[13px]"><span className="font-medium">Outcome:</span> diversified private exposure with managed liquidity.</div>
                <div className="text-[13px]"><span className="font-medium">Watch:</span> concentration by manager, fee layering, capital calls.</div>
              </div>
            </div>

            <h2 className="mt-10">Do Focused Due Diligence</h2>
            <Accordion type="single" collapsible className="not-prose border rounded-lg bg-card">
              <AccordionItem value="strategy">
                <AccordionTrigger className="px-4">Strategy: Where do returns come from?</AccordionTrigger>
                <AccordionContent className="px-4 text-[14px] text-muted-foreground">
                  Look for clear sources of return (carry, yield, operational value-add), a repeatable edge, and risk controls. Ask how results compare across cycles and what could break the thesis.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="team">
                <AccordionTrigger className="px-4">Team: Who executes and how aligned are they?</AccordionTrigger>
                <AccordionContent className="px-4 text-[14px] text-muted-foreground">
                  Verify track record (net of fees), domain experience, and skin-in-the-game. Call references. Check for enforcement actions and turnover.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="terms">
                <AccordionTrigger className="px-4">Terms: What do you give up to get the return?</AccordionTrigger>
                <AccordionContent className="px-4 text-[14px] text-muted-foreground">
                  Fees (management + performance), liquidity/lockups, reporting cadence, governance rights, tax reporting. If you don’t understand a clause, pause.
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="mt-4">
              <div className="inline-flex items-center gap-2 text-sm font-semibold"><Flag className="h-4 w-4 text-red-600" /> Red Flags</div>
              <ul className="mt-1 list-disc pl-5">
                <li>Guaranteed returns or artificial urgency</li>
                <li>Opaque fees or missing disclosures</li>
                <li>Unregistered intermediaries</li>
                <li>Complex structures you can’t explain back</li>
              </ul>
            </div>

            <h2 className="mt-10">TFSA vs RRSP vs Taxable</h2>
            <div className="not-prose grid md:grid-cols-3 gap-3">
              <div className="rounded-lg border bg-card p-3 text-[13px]">
                <div className="font-medium">TFSA</div>
                <div className="text-muted-foreground">Tax-free growth and withdrawals; best for flexibility and post-tax efficiency.</div>
              </div>
              <div className="rounded-lg border bg-card p-3 text-[13px]">
                <div className="font-medium">RRSP</div>
                <div className="text-muted-foreground">Tax deduction now; withdrawals taxed later. Suits higher-income years before retirement.</div>
              </div>
              <div className="rounded-lg border bg-card p-3 text-[13px]">
                <div className="font-medium">Taxable</div>
                <div className="text-muted-foreground">Use when reporting is complex or wrappers aren’t eligible. Coordinate with an accountant.</div>
              </div>
            </div>
            <p className="mt-2"><Link to="/content/can/modern-wealth-playbook#implementation" className="underline text-primary">See tax-efficient account structuring →</Link></p>

            <h2 className="mt-10">30‑Day Action Plan</h2>
            <div className="not-prose grid md:grid-cols-4 gap-3 text-[13px]">
              <div className="rounded-lg border bg-card p-3">
                <div className="font-medium mb-1">Week 1</div>
                <div>Eligibility test • Read the Alternatives section in the Playbook • Define your target allocation.</div>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <div className="font-medium mb-1">Week 2</div>
                <div>Pick your path • Choose TFSA/RRSP/Taxable • Create a shortlist (3 ideas) that fit your objective.</div>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <div className="font-medium mb-1">Week 3</div>
                <div>Run Strategy • Team • Terms • Red Flags for each shortlist item. Ask clarifying questions.</div>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <div className="font-medium mb-1">Week 4</div>
                <div>Deploy a starter allocation • Set calendar reminders for updates • Plan a 90‑day review.</div>
              </div>
            </div>

            <div className="not-prose mt-6 md:mt-8 rounded-xl border border-dashed hover:border-solid border-muted-foreground/30 bg-gradient-to-br from-white to-muted/20 p-4 shadow-sm transition-shadow hover:shadow-xl">
              <div className="grid md:grid-cols-[1fr_auto] items-start gap-4">
                <div className="flex items-start gap-3 text-sm text-foreground">
                  <Sliders className="h-6 w-6 md:h-7 md:w-7 shrink-0 text-muted-foreground opacity-30" aria-hidden="true" />
                  <div>
                    <div className="font-medium">What to do next</div>
                    <div className="text-muted-foreground">1) Take Eligibility Test • 2) Open WealthKit • 3) Pick your path and shortlist 3 ideas.</div>
                  </div>
                </div>
                <div className="justify-self-end self-end flex gap-2">
                  <Link to="/tools/eligibility-test"><Button className="rounded-md bg-[#2D4C7F] hover:bg-[#243a66] text-white">Eligibility Test</Button></Link>
                  <Link to="/portfolio/tools"><Button variant="secondary" className="rounded-md">Open WealthKit</Button></Link>
                </div>
              </div>
            </div>
          </section>

          <div className="not-prose text-[13px] text-muted-foreground mt-12 md:mt-16 mb-10">
            Educational content. Not individualized financial advice. We mention specific platforms for illustration purposes and receive no compensation for these references.
          </div>
          <div className="h-4" aria-hidden="true" />
        </ContentLayout>
      </main>
    </div>
  );
};

export default GettingStartedAlternatives2;
